import { JSX, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next';
import sitecoreRedirect from 'lib/sc-redirect';
import NotFound from 'src/NotFound';
import Layout from 'src/Layout';
import {
  SitecoreContext,
  ComponentPropsContext,
  RouteData,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import { componentBuilder } from 'temp/componentBuilder';
import { ExtendedStyleguideSitecoreContextValue } from 'src/Layout';
import { TrackingProvider } from 'lib/tracking';

export const fetchCache = 'force-no-store';

const SitecorePage = ({
  notFound,
  componentProps,
  layoutData,
  headLinks,
  linksource,
}: SitecorePageProps & { linksource: string | null }): JSX.Element => {  
  const router = useRouter();
  const isRedirecting = useRef(false);
  useEffect(() => {
    if (router.isFallback) return;

    // Handle Experience Editor Fast Refresh
    handleEditorFastRefresh();
  }, [notFound, layoutData?.sitecore?.route]);

  if (!layoutData?.sitecore?.route && !isRedirecting.current) {
    return <NotFound />;
  }

  const isEditing = layoutData.sitecore.context.pageEditing;

  const contextValue: ExtendedStyleguideSitecoreContextValue = {
    sitecore: {
      context: {
        ...layoutData.sitecore.context,
        site: {
          name: layoutData.sitecore.context.site?.name ?? '',
        },
      },
      route:
        (layoutData.sitecore
          .route as ExtendedStyleguideSitecoreContextValue['sitecore']['route']) ||
        ({} as RouteData),
    },
    site: {
      name: layoutData.sitecore.context.site?.name ?? '',
    },
    itemPath: layoutData.sitecore.route?.itemId,
    itemId: layoutData?.sitecore.route?.itemId || undefined,
  };

  return (
    <TrackingProvider value={{ linksource }}>
      <ComponentPropsContext value={componentProps}>
        <SitecoreContext
          componentFactory={componentBuilder.getComponentFactory({
            isEditing,
          })}
          layoutData={layoutData}
        >
          <Layout
            context={contextValue}
            layoutData={layoutData}
            headLinks={headLinks}
          />
        </SitecoreContext>
      </ComponentPropsContext>
    </TrackingProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const defaultLocale = context.defaultLocale || 'en';
  const linksource = context.req.cookies['linksource'] ?? null;

  try {
    const props = await sitecorePagePropsFactory.create(context);

    if (!props.layoutData?.sitecore?.route && context.params?.path) {
      let sourceUrl = '/';

      if (Array.isArray(context.params.path)) {
        sourceUrl += context.params.path.join('/');
      } else {
        sourceUrl += context.params.path;
      }

      const redirect = await sitecoreRedirect(sourceUrl);

      if (redirect) {
        return {
          redirect: {
            destination: redirect.destination,
            statusCode: redirect.permanent ? 301 : 302,
          },
        };
      }

      if (sourceUrl.startsWith('/blog') || sourceUrl.startsWith('/Blog')) {
        return {
          redirect: {
            destination: '/blog',
            statusCode: 301,
          },
        };
      }

      const props = await sitecorePagePropsFactory.create(context);

      if (!props.layoutData?.sitecore?.route) {
        return {
          notFound: true,
        };
      }
    }

    return {
      props: { ...(props || {
        notFound: true,
        layoutData: null,
        componentProps: {},
      }),
      linksource
    },
      notFound: props.notFound,
    };
  } catch (error) {
    console.error('Error in getStaticProps:', error);
    return {
      props: {
        statusCode: 500,
        statusMessage:
          error instanceof Error ? error.message : 'Internal Server Error',
        layoutData: null,
        componentProps: {},
        locale: defaultLocale,
        dictionary: {},
        notFound: true,
        error: true,
      },
    };
  }
};

export default SitecorePage;
